import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export function CyberClicker() {
    const [score, setScore] = useState(0);
    const [clicks, setClicks] = useState<{ id: number; x: number; y: number; value: number }[]>([]);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const handleClick = (e: React.MouseEvent) => {
        const rect = buttonRef.current?.getBoundingClientRect();
        if (!rect) return;

        // Calculate click position relative to button center for effect
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const value = 1;
        setScore(s => s + value);

        // Use random + date for unique ID to avoid collisions in rapid clicking
        const newClick = { id: Date.now() + Math.random(), x, y, value };
        setClicks(prev => [...prev, newClick]);

        // Remove click affect after animation
        setTimeout(() => {
            setClicks(prev => prev.filter(c => c.id !== newClick.id));
        }, 1000);
    };

    return (
        <div className="flex flex-col items-center justify-center p-8 bg-black/40 rounded-xl border border-emerald-500/20 backdrop-blur-sm w-full max-w-md mx-auto aspect-square relative overflow-hidden">

            {/* Background Grid */}
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#10b98110_1px,transparent_1px),linear-gradient(to_bottom,#10b98110_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none" />

            <h2 className="text-2xl font-bold text-emerald-500 mb-8 font-mono tracking-widest">CYBER_CLICKER_V1</h2>

            <div className="text-6xl font-black text-white mb-12 font-mono tabular-nums relative">
                {score.toString().padStart(4, '0')}
                <span className="absolute -top-4 -right-8 text-xs text-emerald-500 animate-pulse">SCORE</span>
            </div>

            <motion.button
                ref={buttonRef}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleClick}
                className="relative w-48 h-48 rounded-full bg-gradient-to-br from-emerald-500 to-sky-600 shadow-[0_0_50px_rgba(16,185,129,0.3)] flex items-center justify-center group overflow-hidden border-4 border-white/10"
            >
                <span className="relative z-10 text-2xl font-bold text-black group-hover:text-white transition-colors">INITIALIZE</span>

                {/* Ripple effect */}
                <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity" />
            </motion.button>

            {/* Floating Numbers */}
            <AnimatePresence>
                {clicks.map(click => (
                    <motion.div
                        key={click.id}
                        initial={{ opacity: 1, y: 0, scale: 0.5 }}
                        animate={{ opacity: 0, y: -100, scale: 1.5 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.8 }}
                        className="absolute font-bold text-emerald-400 pointer-events-none text-2xl"
                        style={{
                            top: '50%',
                            left: '50%',
                            marginLeft: click.x - 96, // center offset (48px / 2 approx)
                            marginTop: click.y - 96
                        }}
                    >
                        +{click.value}
                    </motion.div>
                ))}
            </AnimatePresence>

            <div className="absolute bottom-4 text-xs text-gray-500 font-mono">
                SYSTEM_STATUS: ONLINE
            </div>
        </div>
    );
}
